import { forwardRef, type Ref, useEffect, useMemo, useState } from 'react';

import { type FieldValues, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select as WFSelect, type SelectProps } from '@wf/components';

import { commodityCodeToOption, rimmEntityToOption } from '../utils/rimmEntityToOption';
import { useRimmEntity } from '../hooks/useRimmEntity';

import { type TRimmEntity, type TRimmEntityOption } from '../data/rimmEntityTypes';
import { RimmEntityType } from '../data/rimmEntityEnums';

export type TRimmEntitySelectProps<T extends RimmEntityType> = SelectProps<TRimmEntityOption[T]> & {
  entity: T;
  outerRimmServerURL?: string;
  name: string;
  minSuggest?: number;
  required?: boolean;
  withTag?: boolean;
  tagKey?: keyof TRimmEntity[T];
  hasAllOption?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
};

const RimmEntitySelectComponent = <T extends RimmEntityType>(
  {
    entity,
    outerRimmServerURL,
    minSuggest,
    onInputChange,
    withTag,
    hasAllOption,
    tagKey,
    placeholder,
    isClearable = true,
    value,
    name,
    onChange,
    setValue,
    ...props
  }: TRimmEntitySelectProps<T>,
  ref: Ref<HTMLSelectElement> | null
) => {
  const [searchText, setSearchText] = useState('');
  const { rimmEntities, onSearch, loading } = useRimmEntity(entity, minSuggest, outerRimmServerURL);
  const { t } = useTranslation();

  const options = useMemo(() => {
    if (entity === RimmEntityType.commodity) {
      return commodityCodeToOption(rimmEntities) as TRimmEntityOption[T][];
    } else {
      return rimmEntityToOption(rimmEntities, !!withTag, tagKey);
    }
  }, [entity, rimmEntities, tagKey, withTag]);

  if (hasAllOption) {
    options.unshift({ value: 'ALL', label: t('ALL') } as TRimmEntityOption[T]);
  }

  if (setValue && !minSuggest && options?.length && value) {
    if ((withTag && !value.tag?.label) || (!withTag && !value.label?.description)) {
      const selectedOption = options.find((option) => option.value === value.value);

      if (selectedOption) {
        setValue?.(name, selectedOption);
      }
    }
  }

  useEffect(() => {
    if (setValue && minSuggest && value) {
      if ((withTag && !value.tag?.label) || (!withTag && !value.label?.description)) {
        onSearch(value.value as string, true);

        const selectedOption = options.find((option) => option.value === value.value);

        if (selectedOption) {
          setValue?.(name, selectedOption);
        }
      }
    }
  }, [minSuggest, value, setValue, options, withTag, onSearch, name]);

  const noOptionMessage = () =>
    minSuggest && searchText.length < minSuggest
      ? t('Please type at least {{minSuggest}} characters to see options', { minSuggest })
      : t('No options');

  return (
    <WFSelect
      filterOption={minSuggest ? () => true : undefined}
      {...props}
      onInputChange={(value, actionMeta) => {
        setSearchText(value);
        onInputChange?.(value, actionMeta);
        onSearch(value);
      }}
      placeholder={minSuggest ? `${(placeholder as string) ?? t('Search')} (${t('Min')} ${minSuggest})` : placeholder}
      options={minSuggest && searchText.length < minSuggest ? [] : options}
      loadingMessage={() => t('Loading...')}
      noOptionsMessage={noOptionMessage}
      isClearable={isClearable}
      isLoading={loading}
      onChange={onChange}
      value={value}
      name={name}
      ref={ref}
    />
  );
};

export const RimmEntitySelect = forwardRef(RimmEntitySelectComponent);
